import { keepPreviousData, useQuery } from "@tanstack/preact-query"
import { useState } from "react"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import type { Stats } from "../../server/types"
import { api } from "../api"
import { fmtTokens } from "../format"

const dayOffset = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10)
const DAYS = [...Array(30)].map((_, i) => dayOffset(29 - i))
const ranges = { today: DAYS.slice(-1), "7d": DAYS.slice(-7), "30d": DAYS } as const

type Range = keyof typeof ranges

const EMPTY = { logs: 0, errors: 0, input: 0, cacheRead: 0, output: 0 }

const chartConfig = {
  input: { label: "Input", color: "var(--color-chart-1)" },
  cacheRead: { label: "Cache Read", color: "var(--color-chart-2)" },
  output: { label: "Output", color: "var(--color-chart-3)" },
} satisfies ChartConfig

const fmtTooltipValue = (value: unknown, name?: string | number) => (
  <div className="flex w-full items-center gap-3">
    <span className="size-2.5 rounded-[2px]" style={{ background: `var(--color-${name})` }} />
    <span className="flex-1 text-muted-foreground">
      {chartConfig[name as keyof typeof chartConfig].label}
    </span>
    <span className="font-mono font-medium tabular-nums">{fmtTokens(Number(value))}</span>
  </div>
)

const sum = (rows: Stats[]) =>
  rows.reduce(
    (a, d) => ({
      logs: a.logs + d.logs,
      errors: a.errors + d.errors,
      input: a.input + d.input,
      cacheRead: a.cacheRead + d.cacheRead,
      output: a.output + d.output,
    }),
    EMPTY,
  )

export function StatsPage() {
  const [range, setRange] = useState<Range>("today")
  const [provider, setProvider] = useState("")
  const from = DAYS[0]
  const to = DAYS.at(-1)!
  const { data } = useQuery<{ days: Record<string, Record<string, Stats>>; providers: string[] }>({
    queryKey: ["stats", from, to, provider],
    queryFn: () => api(`/stats?from=${from}&to=${to}&provider=${provider}`).then((r) => r.json()),
    placeholderData: keepPreviousData,
  })
  const [providers, setProviders] = useState<string[]>([])
  if (data?.providers?.length && data.providers.join() !== providers.join()) {
    setProviders(data.providers)
  }
  const stats = data?.days ?? {}

  const daily = DAYS.map((date) => ({ date, ...sum(Object.values(stats[date] ?? {})) }))
  const totals = sum(daily.filter((d) => ranges[range].includes(d.date)))

  return (
    <div className="p-4 pt-0 grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {(Object.keys(ranges) as Range[]).map((r) => (
            <Button
              key={r}
              size="sm"
              variant={range === r ? "default" : "outline"}
              onClick={() => setRange(r)}
            >
              {r === "today" ? "Today" : r === "7d" ? "7 Days" : "30 Days"}
            </Button>
          ))}
        </div>
        <Select
          items={[
            { value: "", label: "All Providers" },
            ...providers.map((p) => ({ value: p, label: p })),
          ]}
          value={provider}
          onValueChange={(v) => setProvider(v ?? "")}
        >
          <SelectTrigger size="sm" aria-label="Provider">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="">All Providers</SelectItem>
            {providers.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard title="Logs" value={totals.logs} />
        <StatCard title="Errors" value={totals.errors} />
        <StatCard
          title="Input Tokens"
          value={
            totals.cacheRead > 0 ? (
              <>
                {fmtTokens(totals.input)}{" "}
                <span className="text-sm text-muted-foreground">
                  ({fmtTokens(totals.cacheRead)})
                </span>
              </>
            ) : (
              fmtTokens(totals.input)
            )
          }
        />
        <StatCard title="Output Tokens" value={fmtTokens(totals.output)} />
      </div>
      <Card>
        <CardHeader>
          <CardDescription>Last 30 days</CardDescription>
          <CardTitle>Token Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="aspect-auto h-64 w-full">
            <AreaChart data={daily} margin={{ left: 4, right: 4, top: 8 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(d: string) => d.slice(5)}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
              />
              <YAxis width={48} tickLine={false} axisLine={false} tickFormatter={fmtTokens} />
              <ChartTooltip content={<ChartTooltipContent formatter={fmtTooltipValue} />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Area
                dataKey="input"
                type="monotone"
                stackId="1"
                fill="var(--color-input)"
                stroke="var(--color-input)"
              />
              <Area
                dataKey="cacheRead"
                type="monotone"
                stackId="1"
                fill="var(--color-cacheRead)"
                stroke="var(--color-cacheRead)"
              />
              <Area
                dataKey="output"
                type="monotone"
                stackId="1"
                fill="var(--color-output)"
                stroke="var(--color-output)"
              />
            </AreaChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  )
}

function StatCard(props: { title: string; value: React.ReactNode }) {
  return (
    <Card size="sm">
      <CardHeader>
        <CardDescription>{props.title}</CardDescription>
        <CardTitle className="text-2xl tabular-nums">{props.value}</CardTitle>
      </CardHeader>
    </Card>
  )
}
